import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: object;
}

const setOrCreateMeta = (selector: string, attr: string, value: string, attrType: 'name' | 'property' = 'name') => {
  let meta = document.querySelector(selector) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attrType, attr);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', value);
};

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogUrl,
  ogTitle,
  ogDescription,
  ogImage,
  twitterTitle,
  twitterDescription,
  twitterImage,
  structuredData,
}: SEOProps) => {
  useEffect(() => {
    const baseTitle = 'CatatCrypto';
    const fullTitle = title
      ? `${baseTitle} | ${title}`
      : `${baseTitle} | Professional Crypto Trade Journal & Analytics`;

    // Set document title
    document.title = fullTitle;

    // Meta description
    if (description) {
      setOrCreateMeta('meta[name="description"]', 'description', description, 'name');
    }

    // Meta keywords
    if (keywords) {
      setOrCreateMeta('meta[name="keywords"]', 'keywords', keywords, 'name');
    }

    // Canonical link
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonical);
    } else if (linkCanonical) {
      linkCanonical.remove();
    }

    // og:url
    let metaOgUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
    if (ogUrl) {
      if (!metaOgUrl) {
        metaOgUrl = document.createElement('meta');
        metaOgUrl.setAttribute('property', 'og:url');
        document.head.appendChild(metaOgUrl);
      }
      metaOgUrl.setAttribute('content', ogUrl);
    } else if (metaOgUrl) {
      metaOgUrl.remove();
    }

    // og:title — fall back to fullTitle if not explicitly set
    setOrCreateMeta('meta[property="og:title"]', 'og:title', ogTitle ?? fullTitle, 'property');

    // og:description — fall back to description
    if (ogDescription || description) {
      setOrCreateMeta('meta[property="og:description"]', 'og:description', ogDescription ?? description ?? '', 'property');
    }

    // og:image
    if (ogImage) {
      setOrCreateMeta('meta[property="og:image"]', 'og:image', ogImage, 'property');
    }

    // twitter:title — fall back to fullTitle if not explicitly set
    setOrCreateMeta('meta[name="twitter:title"]', 'twitter:title', twitterTitle ?? fullTitle, 'name');

    // twitter:description — fall back to description
    if (twitterDescription || description) {
      setOrCreateMeta('meta[name="twitter:description"]', 'twitter:description', twitterDescription ?? description ?? '', 'name');
    }

    // twitter:image
    if (twitterImage) {
      setOrCreateMeta('meta[name="twitter:image"]', 'twitter:image', twitterImage, 'name');
    }

    // JSON-LD structured data
    let scriptJsonLd = document.querySelector('script[type="application/ld+json"]');
    if (structuredData) {
      if (!scriptJsonLd) {
        scriptJsonLd = document.createElement('script');
        scriptJsonLd.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptJsonLd);
      }
      scriptJsonLd.textContent = JSON.stringify(structuredData);
    } else if (scriptJsonLd) {
      scriptJsonLd.remove();
    }

    return () => {
      const currentScript = document.querySelector('script[type="application/ld+json"]');
      if (currentScript) {
        currentScript.remove();
      }
    };
  }, [title, description, keywords, canonical, ogUrl, ogTitle, ogDescription, ogImage, twitterTitle, twitterDescription, twitterImage, structuredData]);
};
